import React from 'react';

import { KbjCard } from '@eika-kortbetjening/types';
import { IconTypes } from '@eika/icon';
import { Label, LabelProps } from '@eika/label';
import { ListPanel } from '@eika/link-panel';

import { CardImage } from './card-image/CardImage';
import { CardListPanelMeta, SiteCoreDictionary } from './types';

import './CardListPanel.scss';

export const getCardLabels = (
    { embossedName }: KbjCard,
    {
        state: { isExpired, isOrdered, isReplaced, isBlocked, isSoftblocked },
        market,
        otherOwnerName,
    }: CardListPanelMeta,
    dictionary: SiteCoreDictionary,
): LabelProps[] => {
    const labels: LabelProps[] = [];

    if (market === 'business') {
        if (embossedName) {
            labels.push({ id: 'card-business-debit-card-embossed-name', text: embossedName, type: 'info' });
        }
        labels.push({ id: 'card-business-debit-card', text: 'Bedriftskort', type: 'info' });
    }

    if (isBlocked) {
        if (isSoftblocked) {
            labels.push({
                id: 'card-block-status-soft-block',
                text: dictionary.cardLabelSoftBlocked,
                type: 'error',
            });
        } else {
            labels.push({
                id: 'card-block-status-blocked',
                text: dictionary.cardLabelBlocked,
                type: 'error',
            });
        }
    }

    if (isOrdered) {
        labels.push({
            id: 'card-production-status-ordered',
            text: dictionary.cardLabelOrdered,
            type: 'info',
        });
    }

    if (isExpired) {
        labels.push({ id: 'card-is-expired', text: dictionary.cardLabelExpired, type: 'warning' });
    }

    if (otherOwnerName) {
        labels.push({
            id: 'card-owner',
            text: `${otherOwnerName} eier kortet`,
            type: 'warning',
        });
    }

    if (isReplaced) {
        labels.push({
            id: 'card-owner-replaced',
            text: dictionary.cardLabelRenewed,
            type: 'warning',
        });
    }

    return labels;
};

export const replaceXWithBullet = (inputString: string): string => inputString.replace(/x/g, '•');

export const getLabelElements = (
    card: KbjCard,
    cardMeta: CardListPanelMeta,
    dictionary: SiteCoreDictionary,
    smallSize = true,
): ReturnType<typeof Label>[] => {
    const labelItems = getCardLabels(card, cardMeta, dictionary);
    return labelItems.map(({ id, text, type }) => (
        <Label key={id} text={text} type={type} size={smallSize ? 'small' : 'standard'} />
    ));
};

export type CardListPanelProps = {
    card: KbjCard;
    cardMeta: CardListPanelMeta;
    labels?: ReturnType<typeof Label>[];
    dictionary: SiteCoreDictionary;
};

export const CardListPanel = ({ card, cardMeta, dictionary, labels = [] }: CardListPanelProps) => (
    <ListPanel
        labels={[...getLabelElements(card, cardMeta, dictionary), ...labels]}
        textTwo={replaceXWithBullet(card.number)}
        title={card.name}
        url={card.url}
        icon={IconTypes.LITEN_PIL_HOEYRE}
        prefixContent={card.imageUrl ? <CardImage imageUrl={card.imageUrl} /> : undefined}
    />
);
